import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketingPageShell } from '@/components/marketing/MarketingPageShell';
import { RedesignButton } from '@/components/marketing/redesign/RedesignButton';
import { RedesignFaq } from '@/components/marketing/redesign/RedesignFaq';
import { RdSection } from '@/components/marketing/redesign/RedesignMotion';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  VESTA_SAAS_TIERS,
  VESTA_PRICING_TRUST_POINTS,
  VESTA_SALES_CALENDAR,
  VESTA_STARTING_PRICE,
  VESTA_CONTACT_EMAIL,
  VESTA_PRICING_FOOTNOTE,
} from '@/brand/vesta-cfo-brand';
import { SCHEDULE_CALL_CTA } from '@/lib/marketing-cta';

const COMPARE_ROWS = [
  { feature: 'Properties', starter: '1', growth: '2–15', enterprise: '15+' },
  { feature: 'Core KPI dashboard', starter: '✓', growth: '✓', enterprise: '✓' },
  { feature: 'Daily AI briefing', starter: '✓', growth: '✓', enterprise: '✓' },
  { feature: 'Mews PMS sync', starter: '✓', growth: '✓', enterprise: '✓' },
  { feature: 'QuickBooks & cash runway', starter: '✓', growth: '✓', enterprise: '✓' },
  { feature: 'Multi-property rollup', starter: '—', growth: '✓', enterprise: '✓' },
  { feature: 'Team seats', starter: '1 user', growth: 'Up to 10', enterprise: 'Unlimited' },
  { feature: 'Weekly owner report email', starter: '—', growth: '✓', enterprise: '✓' },
  { feature: 'Forecasting (90-day)', starter: 'Basic', growth: 'Advanced', enterprise: 'Advanced' },
  { feature: 'Custom integrations / API', starter: '—', growth: '—', enterprise: '✓' },
  { feature: 'SLA & security review', starter: '—', growth: '—', enterprise: '✓' },
  { feature: 'Support', starter: 'Email', growth: 'Priority email + Slack', enterprise: 'Dedicated CSM' },
] as const;

const FAQ_ITEMS = [
  {
    question: "Why isn't every tier priced on the website?",
    answer:
      "Starter is straightforward for a single property. Growth and Enterprise depend on property count, PMS mix, and rollout scope — we quote after a short call so you're not paying for features you don't need.",
  },
  {
    question: "What's included in Starter?",
    answer: `$149/month for one property: full hotel dashboard, daily AI briefing, GM email, anomaly alerts, budget & channel intelligence, Reports hub, QuickBooks sync with cash runway, and manual comp/events CSV. Upgrade to Growth when you add properties or need portfolio rollups.`,
  },
  {
    question: 'Do you replace my accountant?',
    answer:
      'No. Vesta CFO is an operator-facing layer: faster answers from your PMS and financial data, alerts, and exports. Your CPA still owns tax, audit, and statutory reporting.',
  },
  {
    question: 'Which systems do you integrate with?',
    answer:
      'Priority is PMS (Mews live; Cloudbeds, Oracle Opera on roadmap), plus CSV and manual metrics. OTAs, payroll, F&B POS, and accounting connectors expand on the roadmap — Enterprise customers can prioritize specific adapters.',
  },
  {
    question: 'Is my data secure?',
    answer:
      'Encrypted transport and storage, tenant isolation, and industry-standard hosting. Enterprise agreements include additional security documentation and a signed DPA.',
  },
];

const Pricing = () => {
  const { session } = useAuth();
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [hasLegacySubscription, setHasLegacySubscription] = useState(false);

  useEffect(() => {
    document.title = 'Pricing · Vesta CFO';
  }, []);

  useEffect(() => {
    if (!session) return;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('check-subscription');
        if (!error && data?.tier && (data.tier === 'scale' || data.tier === 'ceo')) {
          setHasLegacySubscription(true);
        }
      } catch {
        /* ignore */
      }
    })();
  }, [session]);

  const handleManageLegacyBilling = async () => {
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) window.open(data.url, '_blank');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to open billing portal';
      toast.error(msg);
    } finally {
      setLoadingPortal(false);
    }
  };

  return (
    <MarketingPageShell navActive="pricing">
      <RdSection tight hero style={{ paddingTop: 80, paddingBottom: 0 }}>
        <div className="rd-container-tight" style={{ textAlign: 'center' }}>
          <span className="hero-eye">
            <span className="pulse" />
            Plans &amp; packaging
          </span>
          <h1 className="hero-h" style={{ margin: '20px auto 0', fontSize: 'clamp(40px, 5vw, 64px)' }}>
            Built for <em>operators</em>,<br />not spreadsheets.
          </h1>
          <p className="hero-sub" style={{ margin: '18px auto 0', textAlign: 'center', maxWidth: 580 }}>
            Independent properties get a clear starting point. Multi-property groups and management companies work with
            us on a short call — we scope integrations, properties, and support to match how you run.
          </p>
          <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
            {VESTA_PRICING_TRUST_POINTS.map((point) => (
              <span
                key={point}
                className="hero-eye"
                style={{
                  background: '#fff',
                  borderColor: 'var(--rd-border)',
                  color: 'var(--rd-text-2)',
                }}
              >
                {point}
              </span>
            ))}
          </div>
        </div>
      </RdSection>

      {session && (
        <RdSection tight style={{ paddingTop: 24, paddingBottom: 0 }}>
          <div className="rd-container-tight">
            <div
              style={{
                borderRadius: 14,
                border: '1px solid var(--rd-border)',
                background: '#fff',
                padding: '16px 20px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <p style={{ margin: 0, fontSize: 14, color: 'var(--rd-text-2)' }}>
                Signed in — open your hotel dashboard or manage billing on Integrations.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <RedesignButton variant="outline" to="/dashboard">
                  Dashboard
                </RedesignButton>
                {hasLegacySubscription && (
                  <RedesignButton variant="primary" onClick={handleManageLegacyBilling}>
                    {loadingPortal ? 'Opening…' : 'Billing portal'}
                  </RedesignButton>
                )}
              </div>
            </div>
          </div>
        </RdSection>
      )}

      <RdSection>
        <div className="rd-container">
          <div className="tier-grid">
            {VESTA_SAAS_TIERS.map((tier) => (
              <div key={tier.id} className={`tier${tier.highlight ? ' featured' : ''}`}>
                {tier.highlight && <div className="pop">Most popular</div>}
                <div className="tname">{tier.name}</div>
                <div className="taud">{tier.audience}</div>
                <div className="tprice">
                  {tier.showPrice ? (
                    <>
                      <span className="from">{tier.pricePrefix ?? 'Starting at'}</span>
                      <span className="n">{tier.price}</span>
                      {tier.priceDetail && <div className="u">{tier.priceDetail} · billed monthly</div>}
                    </>
                  ) : (
                    <>
                      <span className="head">{tier.priceHeadline}</span>
                      {tier.priceDetail && <div className="u">{tier.priceDetail}</div>}
                    </>
                  )}
                </div>
                <p className="tdesc">{tier.description}</p>
                {tier.external ? (
                  <RedesignButton
                    variant={tier.highlight ? 'gold' : 'outline'}
                    className="tcta"
                    href={tier.ctaHref}
                    external
                  >
                    {tier.cta} →
                  </RedesignButton>
                ) : (
                  <RedesignButton
                    variant={tier.highlight ? 'gold' : 'primary'}
                    className="tcta"
                    to={tier.ctaHref === '/auth' ? '/auth?redirect=/onboarding' : tier.ctaHref}
                  >
                    {tier.cta} →
                  </RedesignButton>
                )}
                <ul className="tlist">
                  {tier.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p
            style={{
              marginTop: 40,
              textAlign: 'center',
              fontSize: 13.5,
              color: 'var(--rd-text-2)',
              maxWidth: 680,
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.65,
            }}
          >
            {VESTA_PRICING_FOOTNOTE}
          </p>

          <div className="compare">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '38%' }}>Feature</th>
                  <th>Starter</th>
                  <th className="featured-col">Growth</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.feature}>
                    <td className="feature">{row.feature}</td>
                    <td className={row.starter === '—' ? 'dash' : 'check'}>{row.starter}</td>
                    <td className={`${row.growth === '—' ? 'dash' : 'check'} featured-col`}>{row.growth}</td>
                    <td className={row.enterprise === '—' ? 'dash' : 'check'}>{row.enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 96, maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div className="bloc-eye" style={{ justifyContent: 'center' }}>
                <span className="num">— 03</span>Common questions
              </div>
              <h2 className="bloc-h" style={{ margin: '0 auto', fontSize: 36 }}>
                Answers before the call.
              </h2>
            </div>
            <RedesignFaq items={FAQ_ITEMS} />
          </div>

          <div style={{ marginTop: 64, textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: 'var(--rd-text-2)', marginBottom: 16 }}>
              Not sure which plan fits? Book a 30-minute walkthrough — no pressure, no generic demo script.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <RedesignButton variant="gold" href={VESTA_SALES_CALENDAR} external>
                {SCHEDULE_CALL_CTA}
              </RedesignButton>
              <RedesignButton variant="outline" href={`mailto:${VESTA_CONTACT_EMAIL}`}>
                {VESTA_CONTACT_EMAIL}
              </RedesignButton>
            </div>
            <p style={{ marginTop: 16, fontSize: 13, color: 'var(--rd-text-3)' }}>
              Starter from {VESTA_STARTING_PRICE}/mo ·{' '}
              <Link to="/features" style={{ color: 'var(--vesta-navy-muted)' }}>
                See features
              </Link>
            </p>
          </div>
        </div>
      </RdSection>
    </MarketingPageShell>
  );
};

export default Pricing;
